import React from "react";
import { useTranslation } from "react-i18next";
import type { GetRef } from "antd";

import { App, Select, SelectProps, Spin } from "antd";
import { debounce } from "lodash";
import { useSelector } from "react-redux";

import request from "../../api/request/http-service";
import { SERVER_ADDRESSES } from "../../config/serverAddressesConfig";
import {
  makeSelectAuthenticatedUserInfo,
  makeSelectCarriers,
  makeSelectClients,
  makeSelectHost,
  makeSelectRegularBitrixCompanies,
} from "../../containers/App/selectors";
import CompanyTypes from "../../dto/Company/CompanyTypes";
import BitrixCompanyDto from "../../dto/Dictionaries/BitrixCompanyDto";
import { BitrixCompanyDataConverter, BitrixCompanyModel } from "../../viewModels/Dictionaries/BitrixCompanyModel";
import endPoints from "../../api/apiServices/dictionary/controllers/bitrixClients/bitrixClients.endpoints";
import { SearchBitrixClientsRequestDto } from "../../api/apiServices/dictionary/controllers/bitrixClients/bitrixClients.requestsDto";
import { uniqueCompanies } from "../../utils/uniqueCompanies";
import BlackListedCompanyName from "../BlackListedCompanyName";
import contractorEndPoints from "../../api/apiServices/dictionary/controllers/contractors/contractors.endpoints";
import ContractorNameInfoDto from "../../dto/Settings/ContractorDto";
import { LocalCompanyDataConverter } from "../../viewModels/Dictionaries/LocalCompanyDataConverter";
import { isInternalOffice } from "../../utils/isInternalOffice";
import { fetchContractorBasicInfo } from "../../api/apiServices/dictionary/controllers/contractors/contractors.api";

type BitrixCompanyOption = {
  label: React.ReactNode;
  value: number;
  data: BitrixCompanyModel;
};

type BitrixCompanySelectValue = {
  label: React.ReactNode;
  value: number;
};

type Props = Omit<
  SelectProps<BitrixCompanySelectValue | Array<BitrixCompanySelectValue>, BitrixCompanyOption>,
  "defaultValue" | "filterOption" | "labelInValue" | "loading" | "mode" | "onChange" | "onSearch" | "options" | "showSearch" | "value"
> & {
  defaultCompanies?: BitrixCompanyModel[];
  companyType?: CompanyTypes;
  localOnly?: boolean;
  limit?: number;
} & (
    | { multiple?: false; value?: BitrixCompanyModel; onChange?: (value?: BitrixCompanyModel) => void }
    | { multiple: true; value?: Array<BitrixCompanyModel>; onChange?: (value: Array<BitrixCompanyModel>) => void }
  );

function renderCompanyLabel(company: BitrixCompanyModel): React.ReactNode {
  return company.isBlackList ? <BlackListedCompanyName name={company.name} /> : company.name;
}

async function searchBitrixClients({
  abortController,
  companyType,
  host,
  internal,
  limit,
  query,
}: SearchBitrixClientsRequestDto & {
  abortController: AbortController;
  host: "WEB" | "BITRIX";
  internal: boolean;
}): Promise<Array<BitrixCompanyDto>> {
  if (internal) {
    const companies = (await request(
      {
        server: SERVER_ADDRESSES.DICTIONARY_API,
        controller: contractorEndPoints.search,
        requestMethod: "get",
        query: {
          query,
          limit: limit ?? 20,
          contractorTypes: companyType === "CLIENT" || companyType === "CARRIER" ? [companyType] : undefined,
        },
        body: undefined,
        headers: undefined,
        abortController,
      },
      host,
    )) as ContractorNameInfoDto[];
    return companies.map(LocalCompanyDataConverter.fromNameInfo);
  }
  const q: SearchBitrixClientsRequestDto = {
    query,
  };

  if (companyType) {
    q.companyType = companyType;
  }

  if (limit !== undefined) {
    q.limit = limit;
  }

  return (await request(
    {
      server: SERVER_ADDRESSES.DICTIONARY_API,
      controller: endPoints.search,
      requestMethod: "get",
      query: q,
      body: undefined,
      headers: undefined,
      abortController,
    },
    host,
  )) as Array<BitrixCompanyDto>;
}

const BitrixCompanySelect = React.memo(
  React.forwardRef<GetRef<typeof Select>, Props>(function BitrixCompanySelect(componentProps, ref) {
    const {
      allowClear = true,
      companyType,
      localOnly = false,
      defaultCompanies,
      limit,
      notFoundContent,
      onChange: _onChange,
      style,
      value,
      multiple,
      optionRender,
      onOpenChange,
      ...props
    } = componentProps;
    const host = useSelector(makeSelectHost());
    const { t } = useTranslation("translations");
    const { message } = App.useApp();
    const regularCompanies = useSelector(makeSelectRegularBitrixCompanies());
    const user = useSelector(makeSelectAuthenticatedUserInfo());
    const internal = localOnly || isInternalOffice(user?.company.settings.officeType);
    const clients = useSelector(makeSelectClients());
    const carriers = useSelector(makeSelectCarriers());
    const localCompanies = React.useMemo(
      () =>
        (companyType === "CLIENT" ? clients : companyType === "CARRIER" ? carriers : [...clients, ...carriers]).map(
          (company): BitrixCompanyModel => ({
            id: company.id,
            name: company.name,
            isBlackList: company.isBlackList,
            address: [],
            contracts: [],
          }),
        ),
      [companyType, clients, carriers],
    );

    const [fetching, setFetching] = React.useState(false);
    const [selecting, setSelecting] = React.useState(false);
    const [searchFailed, setSearchFailed] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<Array<BitrixCompanyOption>>([]);
    const [searchQuery, setSearchQuery] = React.useState("");
    const abortRef = React.useRef<AbortController>();
    const fetchRef = React.useRef(0);
    const selectionAbortRef = React.useRef<AbortController>();
    const selectionRef = React.useRef(0);

    const selectedCompanies = React.useMemo(() => (Array.isArray(value) ? value : value ? [value] : []), [value]);
    const selectedValues = React.useMemo(
      () => selectedCompanies.map((company) => ({ label: renderCompanyLabel(company), value: company.id })),
      [selectedCompanies],
    );

    const defaultOptions = React.useMemo(
      () =>
        uniqueCompanies(
          defaultCompanies === undefined
            ? []
            : [
                ...defaultCompanies,
                ...(internal
                  ? localCompanies
                  : regularCompanies.filter((company) => !companyType || company.companyTypes?.includes(companyType))),
              ],
        ).map((company) => ({
          label: renderCompanyLabel(company),
          value: company.id,
          data: company,
        })),
      [companyType, defaultCompanies, regularCompanies, internal, localCompanies],
    );
    const displayedOptions = searchQuery.trim() ? options : defaultOptions;

    const debounceFetcher = React.useMemo(() => {
      const loadOptions = (query: string, fetchId: number) => {
        const abortController = new AbortController();
        abortRef.current = abortController;

        searchBitrixClients({
          abortController,
          companyType,
          host,
          internal,
          limit,
          query,
        })
          .then((companies) => {
            if (fetchId !== fetchRef.current || abortController.signal.aborted) {
              return;
            }

            setOptions(
              companies.map((company) => ({
                label: renderCompanyLabel(BitrixCompanyDataConverter.convertContractor(company)),
                value: company.id,
                data: BitrixCompanyDataConverter.convertContractor(company),
              })),
            );
          })
          .catch(() => {
            if (fetchId !== fetchRef.current || abortController.signal.aborted) {
              return;
            }

            setOptions([]);
            setSearchFailed(true);
          })
          .finally(() => {
            if (fetchId !== fetchRef.current || abortController.signal.aborted) {
              return;
            }

            setFetching(false);
          });
      };

      return debounce(loadOptions, 300);
    }, [companyType, host, internal, limit]);

    React.useEffect(() => {
      debounceFetcher.cancel();
      fetchRef.current += 1;
      abortRef.current?.abort();
      setOptions([]);
      setSearchQuery("");
      setFetching(false);
      setSearchFailed(false);
      selectionRef.current += 1;
      selectionAbortRef.current?.abort();
      setSelecting(false);
      return () => {
        debounceFetcher.cancel();
        abortRef.current?.abort();
        selectionRef.current += 1;
        selectionAbortRef.current?.abort();
      };
    }, [debounceFetcher, user?.company.id]);

    const handleSearch = React.useCallback(
      (searchText: string) => {
        const query = searchText.trim();

        setSearchQuery(searchText);
        setSearchFailed(false);
        fetchRef.current += 1;
        const fetchId = fetchRef.current;

        abortRef.current?.abort();
        setOptions([]);

        if (!query) {
          debounceFetcher.cancel();
          setFetching(false);
          setOpen(defaultOptions.length > 0);
          return;
        }

        setFetching(true);
        setOpen(true);
        debounceFetcher(query, fetchId);
      },
      [debounceFetcher, defaultOptions.length],
    );

    const handleOpenChange = React.useCallback(
      (nextOpen: boolean) => {
        if (!nextOpen && defaultCompanies !== undefined) handleSearch("");
        setOpen(nextOpen);
      },
      [defaultCompanies, handleSearch],
    );

    const popupOpen = open && (!!searchQuery.trim() || fetching || displayedOptions.length > 0);
    React.useEffect(() => {
      onOpenChange?.(popupOpen);
    }, [onOpenChange, popupOpen]);

    const handleChange: SelectProps<BitrixCompanySelectValue | Array<BitrixCompanySelectValue>, BitrixCompanyOption>["onChange"] = async (
      selectValue,
    ) => {
      const values = Array.isArray(selectValue) ? selectValue : selectValue ? [selectValue] : [];
      const companiesById = new Map(selectedCompanies.map((company) => [company.id, company]));
      displayedOptions.forEach((option) => companiesById.set(option.value, option.data));
      let companies = values.map((item) => companiesById.get(item.value)).filter((company) => company !== undefined);
      const selectionId = ++selectionRef.current;
      selectionAbortRef.current?.abort();
      setSelecting(false);

      if (internal && companies.length > 0) {
        const abortController = new AbortController();
        selectionAbortRef.current = abortController;
        setSelecting(true);
        try {
          companies = await Promise.all(
            companies.map(async (company) => {
              const dto = await fetchContractorBasicInfo(company.id, host, abortController);
              if (dto.id !== company.id) throw new Error("Contractor response does not match selection");
              return BitrixCompanyDataConverter.convertContractor(LocalCompanyDataConverter.fromBasicInfo(dto));
            }),
          );
          if (selectionId !== selectionRef.current || abortController.signal.aborted) return;
        } catch {
          if (selectionId === selectionRef.current && !abortController.signal.aborted) message.error(t("error"));
          return;
        } finally {
          if (selectionId === selectionRef.current) setSelecting(false);
        }
      }

      if (componentProps.multiple) {
        componentProps.onChange?.(companies);
      } else {
        componentProps.onChange?.(companies[0]);
      }
      if (defaultCompanies !== undefined) {
        handleSearch("");
        if (!componentProps.multiple) setOpen(false);
      }
    };

    const renderOption: SelectProps<BitrixCompanySelectValue, BitrixCompanyOption>["optionRender"] = (option) => option.data.label;

    return (
      <Select<BitrixCompanySelectValue | Array<BitrixCompanySelectValue>, BitrixCompanyOption>
        {...props}
        ref={ref}
        allowClear={allowClear}
        labelInValue
        mode={multiple ? "multiple" : undefined}
        loading={fetching || selecting}
        notFoundContent={fetching ? <Spin size="small" /> : searchFailed ? t("error") : notFoundContent}
        onChange={handleChange}
        onOpenChange={handleOpenChange}
        open={popupOpen}
        optionRender={optionRender ?? renderOption}
        options={displayedOptions}
        showSearch={{
          searchValue: defaultCompanies !== undefined ? searchQuery : undefined,
          filterOption: false,
          onSearch: handleSearch,
        }}
        style={{ width: "100%", ...style }}
        value={multiple ? selectedValues : selectedValues[0]}
      />
    );
  }),
);

export default BitrixCompanySelect;
